import { ErrorHandler, inject } from '@angular/core';
import { environment } from '@environments/environment';
import { LoggerService } from './logger.service';
import { NotificationService } from './notification.service';
import { CategorizedError, ErrorCategory } from '../models/notification.models';

/**
 * Global error handler that intercepts all uncaught errors.
 * Logs context, categorizes errors, and shows user-friendly notifications.
 */
export class GlobalErrorHandler implements ErrorHandler {
  private readonly logger = inject(LoggerService);
  private readonly notification = inject(NotificationService);

  handleError(error: unknown): void {
    const categorized = this.categorizeError(error);

    // Log with full context
    if (environment.production) {
      // Production: minimal details, no stack trace to console
      this.logger.error(`[${categorized.category}] ${categorized.message}`, {
        category: categorized.category,
        timestamp: categorized.timestamp.toISOString(),
      });
    } else {
      // Development: full details including stack trace
      this.logger.error(`[${categorized.category}] ${categorized.message}`, {
        category: categorized.category,
        timestamp: categorized.timestamp.toISOString(),
        originalError: categorized.originalError,
        stack: error instanceof Error ? error.stack : undefined,
      });
    }

    // Show user notification for errors that affect functionality
    if (categorized.category !== 'user-cancellation') {
      this.notification.error(categorized.userMessage);
    }
  }

  private categorizeError(error: unknown): CategorizedError {
    const timestamp = new Date();
    const originalError = error;

    // Handle HttpErrorResponse
    if (this.isHttpError(error)) {
      const status = error.status;
      if (status === 401 || status === 403) {
        return {
          originalError,
          category: 'authentication',
          message: `Authentication error: ${error.message}`,
          userMessage: 'Your session has expired. Please log in again.',
          timestamp,
        };
      }
      if (status === 0) {
        return {
          originalError,
          category: 'network',
          message: `Network error: ${error.message}`,
          userMessage: 'Unable to connect to the server. Please check your connection.',
          timestamp,
        };
      }
      if (status >= 400 && status < 500) {
        return {
          originalError,
          category: 'validation',
          message: `Validation error (${status}): ${error.message}`,
          userMessage: 'There was a problem with your request. Please check your input and try again.',
          timestamp,
        };
      }
      return {
        originalError,
        category: 'recoverable',
        message: `Server error (${status}): ${error.message}`,
        userMessage: 'Something went wrong on our end. Please try again later.',
        timestamp,
      };
    }

    // Handle Error objects
    if (error instanceof Error) {
      // Navigation cancellation is not an error
      if (error.message?.includes('NavigationCancel') || error.message?.includes('NavigationCancellationError')) {
        return {
          originalError,
          category: 'user-cancellation',
          message: error.message,
          userMessage: '',
          timestamp,
        };
      }

      // TypeError, ReferenceError etc. are fatal
      return {
        originalError,
        category: 'fatal',
        message: error.message,
        userMessage: 'An unexpected error occurred. Please try again.',
        timestamp,
      };
    }

    // Unknown error type
    return {
      originalError,
      category: 'fatal',
      message: String(error),
      userMessage: 'An unexpected error occurred. Please try again.',
      timestamp,
    };
  }

  private isHttpError(error: unknown): error is { status: number; message: string; error: unknown } {
    return (
      typeof error === 'object' &&
      error !== null &&
      'status' in error &&
      'message' in error
    );
  }
}
